import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  BookOpen, 
  Clock, 
  ExternalLink, 
  FileText,
  Calendar,
  Video, 
  Check, 
  AlertCircle,
  MessageSquare 
} from 'lucide-react';
import { Lesson, LessonFormat } from '@/types/ai-devquest';
import { aiDevQuestService } from '@/services/aiDevQuestService';
import { LessonComments } from './LessonComments';
import { commentsService } from '@/services/commentsService';
import { ScrollArea } from '@/components/ui/scroll-area';

interface LessonDialogProps {
  lesson: Lesson;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (lessonId: string) => void;
  isCompleted?: boolean;
}

/**
 * Dialog component for displaying lesson content
 */
export const LessonDialog: React.FC<LessonDialogProps> = ({
  lesson,
  isOpen,
  onClose,
  onComplete,
  isCompleted = false
}) => {
  const [activeTab, setActiveTab] = useState<string>('content');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localCompleted, setLocalCompleted] = useState(isCompleted);
  const [commentCount, setCommentCount] = useState(0);
  const [isContentScrollable, setIsContentScrollable] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  
  // Refs for scroll areas
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Fetch comment count
  useEffect(() => {
    const fetchCommentCount = async () => {
      try {
        const count = await commentsService.getLessonCommentCount(lesson.id);
        setCommentCount(count);
      } catch (err) {
        console.error('Error fetching comment count:', err);
      }
    };
    
    fetchCommentCount();
  }, [lesson.id]);
  
  // Handle marking lesson as complete
  const handleMarkComplete = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Get current user email from auth
      const auth = await import('@/config/firebase').then(module => module.auth);
      const userEmail = auth.currentUser?.email;
      
      if (!userEmail) {
        throw new Error('User not authenticated');
      }
      
      // Mark lesson as completed in Firebase
      await aiDevQuestService.markLessonAsCompleted(userEmail, lesson.id);
      
      // Update local state
      setLocalCompleted(true);
      
      // Notify parent component
      onComplete(lesson.id);
    } catch (err) {
      console.error('Error marking lesson as complete:', err);
      setError('Failed to mark lesson as complete. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format estimated time
  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours} hr`;
    }
    
    return `${hours} hr ${remainingMinutes} min`;
  };
  
  // Render Vimeo video embed
  const renderVimeoEmbed = (url: string) => {
    // Extract Vimeo video ID from URL
    const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
    const match = url.match(vimeoRegex);
    
    if (!match) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Invalid Vimeo URL</AlertDescription>
        </Alert>
      );
    }
    
    const videoId = match[1];
    
    return (
      <div className="aspect-video w-full mb-4">
        <iframe
          src={`https://player.vimeo.com/video/${videoId}?h=0&autopause=0&player_id=0&app_id=58479`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="w-full h-full rounded-md"
          title={lesson.title}
        ></iframe>
      </div>
    );
  };
  
  // Render Google Doc embed
  const renderGoogleDocEmbed = (url: string) => {
    return (
      <div className="aspect-[4/3] w-full mb-4">
        <iframe
          src={`${url}?embedded=true`}
          className="w-full h-full rounded-md"
          title={lesson.title}
        ></iframe>
      </div>
    );
  };
  
  // Get and format the lesson date (using updatedAt or createdAt)
  const getLessonDate = (lesson: Lesson): string => {
    // Try updatedAt first, then createdAt
    const timestamp = lesson.updatedAt || lesson.createdAt;
    
    if (!timestamp) {
      return 'No date';
    }
    
    let date;
    if (timestamp.toDate) {
      // Firebase Timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return 'No date';
    }
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Check if content is scrollable
  const checkScrollability = () => {
    if (!scrollAreaRef.current) return;
    
    // Find the scroll container (the element with scrollbar)
    const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    
    if (scrollContainer) {
      // Check if content is scrollable (content height > container height)
      const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight + 10;
      setIsContentScrollable(isScrollable);
      setShowScrollIndicator(isScrollable);
    }
  };
  
  // Check scrollability after component mounts and when tab changes
  useEffect(() => {
    // Use setTimeout to ensure content has rendered
    const timer = setTimeout(checkScrollability, 100);
    return () => clearTimeout(timer);
  }, [activeTab, isOpen]);
  
  // Handle scroll events to detect when user has scrolled to bottom
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    
    // Check if content is scrollable (content height > container height)
    const isScrollable = target.scrollHeight > target.clientHeight + 10; // Adding a small threshold
    setIsContentScrollable(isScrollable);
    
    // If content is not scrollable, don't show the indicator
    if (!isScrollable) {
      setShowScrollIndicator(false);
      return;
    }
    
    // Check if scrolled to bottom (with a small threshold)
    const isAtBottom = 
      Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 10;
    // Hide indicator when scrolled to bottom
    setShowScrollIndicator(!isAtBottom);
  };
  
  // Reset scroll state when tab changes
  useEffect(() => {
    setIsContentScrollable(false);
  }, [activeTab]);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[98vh] dark:bg-gray-800 dark:border-gray-700 flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center">
            <BookOpen className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
            {lesson.title}
          </DialogTitle>
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mt-2">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
  
              <Clock className="h-4 w-4 mr-1" />
                {formatTime(lesson.estimatedTime)}
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {getLessonDate(lesson)}
              </div>
            </div>
            
            {/* Format Icons */}
            <div className="flex space-x-3">
              {lesson.formats.includes(LessonFormat.VIDEO) && (
                <div title="Video" className="flex items-center">
                  <Video className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">Video</span>
                </div>
              )}
              {lesson.formats.includes(LessonFormat.DOCUMENT) && (
                <div title="Document" className="flex items-center">
                  <FileText className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">Document</span>
                </div>
              )}
              {lesson.formats.includes(LessonFormat.LINK) && (
                <div title="External Link" className="flex items-center">
                  <ExternalLink className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">Links</span>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>
        
        {/* Lesson Content */}
        <Tabs defaultValue="content" value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden mt-2">
          <div className="flex justify-center mb-6 shrink-0">
            <TabsList className="bg-white dark:bg-gray-800 rounded-full shadow-lg p-1.5 flex gap-1 mx-auto border border-gray-100 dark:border-gray-700 w-auto">
            <TabsTrigger value="content" className="rounded-full px-5 py-2 data-[state=active]:bg-gray-600 dark:data-[state=active]:bg-gray-700 data-[state=active]:text-white transition-all duration-200">
              <span className="font-medium">Content</span>
            </TabsTrigger>
            {lesson.content.notes && (
              <TabsTrigger value="notes" className="rounded-full px-5 py-2 data-[state=active]:bg-gray-600 dark:data-[state=active]:bg-gray-700 data-[state=active]:text-white transition-all duration-200">
                <span className="font-medium">Notes</span>
              </TabsTrigger>
            )}
            {lesson.content.externalLinks && lesson.content.externalLinks.length > 0 && (
              <TabsTrigger value="links" className="rounded-full px-5 py-2 data-[state=active]:bg-gray-600 dark:data-[state=active]:bg-gray-700 data-[state=active]:text-white transition-all duration-200">
                <span className="font-medium">Links</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="comments" className="rounded-full px-5 py-2 data-[state=active]:bg-gray-600 dark:data-[state=active]:bg-gray-700 data-[state=active]:text-white transition-all duration-200 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="font-medium">Comments</span>
              {commentCount > 0 && (
                <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 data-[state=active]:bg-blue-400 data-[state=active]:text-white text-xs px-2 py-0.5 rounded-full font-semibold ml-1">
                  {commentCount}
                </span>
              )}
              </TabsTrigger>
            </TabsList>
          </div>
          
          <div className="relative flex-1 overflow-hidden">
            <TabsContent value="content" className="mt-0 h-full">
              <ScrollArea 
                className="h-[calc(80vh-180px)] pr-4" 
                type="always" 
                onScrollCapture={handleScroll}
                ref={scrollAreaRef as React.RefObject<HTMLDivElement>}
              >
                <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                  {lesson.content.videoUrl && (
                    renderVimeoEmbed(lesson.content.videoUrl)
                  )}
                  
                  {/* Document Content */}
                  {lesson.content.documentUrl && (
                    renderGoogleDocEmbed(lesson.content.documentUrl)
                  )}
                  
                  {/* Description */}
                  <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {lesson.description}
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>
            
            {/* Notes Tab */}
            {lesson.content.notes && (
              <TabsContent value="notes" className="mt-0 h-full">
                <ScrollArea 
                  className="h-[calc(80vh-180px)] pr-4" 
                  type="always" 
                  onScrollCapture={handleScroll}
                  ref={activeTab === 'notes' ? (scrollAreaRef as React.RefObject<HTMLDivElement>) : undefined}
                >
                  <div className="bg-white dark:bg-gray-900 p-4 rounded-lg prose dark:prose-invert max-w-none whitespace-pre-wrap">
                    {lesson.content.notes}
                  </div>
                </ScrollArea>
              </TabsContent>
            )}
            
            {/* Links Tab */}
            {lesson.content.externalLinks && lesson.content.externalLinks.length > 0 && (
              <TabsContent value="links" className="mt-0 h-full">
                <ScrollArea 
                  className="h-[calc(80vh-180px)] pr-4" 
                  type="always" 
                  onScrollCapture={handleScroll}
                  ref={activeTab === 'links' ? (scrollAreaRef as React.RefObject<HTMLDivElement>) : undefined}
                >
                  <div className="bg-white dark:bg-gray-900 p-4 rounded-lg space-y-2">
                    <h3 className="text-lg font-semibold mb-2 dark:text-gray-200">External Resources</h3>
                    <ul className="space-y-2">
                      {lesson.content.externalLinks.map((link, index) => (
                        <li key={index}>
                          <a 
                            href={link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            {link}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollArea>
              </TabsContent>
            )}

            {/* Comments Tab */}
            <TabsContent value="comments" className="mt-0 h-full">
              <ScrollArea 
                className="h-[calc(80vh-180px)] pr-4" 
                type="always" 
                onScrollCapture={handleScroll}
                ref={activeTab === 'comments' ? (scrollAreaRef as React.RefObject<HTMLDivElement>) : undefined}
              >
                <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                  <div className="p-2">
                    <LessonComments
                      lessonId={lesson.id}
                      onCommentCountChange={setCommentCount}
                    />
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>
            
            {/* Scroll indicator - positioned outside of tabs content but inside the tabs container */}
            {isContentScrollable && showScrollIndicator && (
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-gray-900 to-transparent pointer-events-none flex items-end justify-center pb-2 z-10">
                <span className="text-sm text-gray-500 dark:text-gray-400 bg-white/80 dark:bg-gray-800/80 px-3 py-1 rounded-full shadow-sm">
                  Scroll for more content
                </span>
              </div>
            )}
          </div>
        </Tabs>
        
        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <DialogFooter className="mt-4 shrink-0">
          {!localCompleted ? (
            <Button 
              onClick={handleMarkComplete} 
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 dark:text-white"
            >
              <Check className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Marking as Complete...' : 'Mark as Complete'}
            </Button>
          ) : (
            <div className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-3 py-2 rounded-md flex items-center">
              <Check className="h-4 w-4 mr-2" />
              Completed
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};